import { WaveConfig } from './configService';
import { WavePoolPreset } from './configService';

/**
 * PresetLoader - Dynamically loads preset configurations from JSON files
 * Organizes presets by category and supports both Wave and WavePool presets
 */

export interface PresetMetadata {
  id: string;
  name: string;
  category: string;
  fileName: string;
}

class PresetLoader {
  private wavePresetCache: Map<string, WaveConfig> = new Map();
  private wavePoolPresetCache: Map<string, WavePoolPreset> = new Map();
  private waveMetadataCache: PresetMetadata[] | null = null;
  private wavePoolMetadataCache: PresetMetadata[] | null = null;

  /**
   * Load all Wave presets from JSON files
   */
  async loadWavePresets(): Promise<WaveConfig[]> {
    try {
      const response = await fetch('/presets/wave/index.json');
      if (!response.ok) {
        throw new Error(`Failed to load wave presets index: ${response.statusText}`);
      }
      
      const index = await response.json() as PresetMetadata[];
      const presets: WaveConfig[] = [];

      for (const metadata of index) {
        try {
          const preset = await this.loadWavePreset(metadata.id);
          if (preset) {
            presets.push(preset);
          }
        } catch (error) {
          console.warn(`Failed to load wave preset ${metadata.id}:`, error);
        }
      }

      return presets;
    } catch (error) {
      console.error('Error loading wave presets:', error);
      return [];
    }
  }

  /**
   * Load a single Wave preset by ID
   */
  async loadWavePreset(id: string): Promise<WaveConfig | null> {
    // Check cache first
    if (this.wavePresetCache.has(id)) {
      return this.wavePresetCache.get(id) || null;
    }

    try {
      // Load from JSON file
      const response = await fetch(`/presets/wave/${id}.json`);
      if (!response.ok) {
        console.warn(`Preset file not found: /presets/wave/${id}.json`);
        return null;
      }

      const preset = await response.json() as WaveConfig;
      this.wavePresetCache.set(id, preset);
      return preset;
    } catch (error) {
      console.error(`Error loading wave preset ${id}:`, error);
      return null;
    }
  }

  /**
   * Load all WavePool presets from JSON files
   */
  async loadWavePoolPresets(): Promise<WavePoolPreset[]> {
    try {
      const response = await fetch('/presets/wavepool/index.json');
      if (!response.ok) {
        throw new Error(`Failed to load wavepool presets index: ${response.statusText}`);
      }
      
      const index = await response.json() as PresetMetadata[];
      const presets: WavePoolPreset[] = [];

      for (const metadata of index) {
        try {
          const preset = await this.loadWavePoolPreset(metadata.id);
          if (preset) {
            presets.push(preset);
          }
        } catch (error) {
          console.warn(`Failed to load wavepool preset ${metadata.id}:`, error);
        }
      }

      return presets;
    } catch (error) {
      console.error('Error loading wavepool presets:', error);
      return [];
    }
  }

  /**
   * Load a single WavePool preset by ID
   */
  async loadWavePoolPreset(id: string): Promise<WavePoolPreset | null> {
    // Check cache first
    if (this.wavePoolPresetCache.has(id)) {
      return this.wavePoolPresetCache.get(id) || null;
    }

    try {
      const response = await fetch(`/presets/wavepool/${id}.json`);
      if (!response.ok) {
        console.warn(`Preset file not found: /presets/wavepool/${id}.json`);
        return null;
      }

      const preset = await response.json() as WavePoolPreset;
      this.wavePoolPresetCache.set(id, preset);
      return preset;
    } catch (error) {
      console.error(`Error loading wavepool preset ${id}:`, error);
      return null;
    }
  }

  /**
   * Get presets filtered by category
   */
  async getWavePresetsByCategory(category: string): Promise<WaveConfig[]> {
    const allPresets = await this.loadWavePresets();
    return allPresets.filter(p => p.category === category);
  }

  /**
   * Get presets filtered by category
   */
  async getWavePoolPresetsByCategory(category: string): Promise<WavePoolPreset[]> {
    const allPresets = await this.loadWavePoolPresets();
    return allPresets.filter(p => p.category === category);
  }

  /**
   * Search presets by name or tags
   */
  async searchWavePresets(query: string): Promise<WaveConfig[]> {
    const allPresets = await this.loadWavePresets();
    const lowerQuery = query.toLowerCase();
    
    return allPresets.filter(p => 
      p.name.toLowerCase().includes(lowerQuery) ||
      p.description?.toLowerCase().includes(lowerQuery) ||
      (p.tags && p.tags.some(tag => tag.toLowerCase().includes(lowerQuery)))
    );
  }

  /**
   * Search presets by name or tags
   */
  async searchWavePoolPresets(query: string): Promise<WavePoolPreset[]> {
    const allPresets = await this.loadWavePoolPresets();
    const lowerQuery = query.toLowerCase();
    
    return allPresets.filter(p => 
      p.name.toLowerCase().includes(lowerQuery) ||
      p.description?.toLowerCase().includes(lowerQuery) ||
      (p.tags && p.tags.some(tag => tag.toLowerCase().includes(lowerQuery)))
    );
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.wavePresetCache.clear();
    this.wavePoolPresetCache.clear();
    this.waveMetadataCache = null;
    this.wavePoolMetadataCache = null;
  }

  /**
   * Get unique categories from all presets
   */
  async getWavePresetCategories(): Promise<string[]> {
    const allPresets = await this.loadWavePresets();
    const categories = new Set(allPresets.map(p => p.category).filter(Boolean));
    return Array.from(categories).sort();
  }

  /**
   * Get unique categories from all presets
   */
  async getWavePoolPresetCategories(): Promise<string[]> {
    const allPresets = await this.loadWavePoolPresets();
    const categories = new Set(allPresets.map(p => p.category).filter(Boolean));
    return Array.from(categories).sort();
  }
}

export const presetLoader = new PresetLoader();
